import { createFileRoute, Link } from "@tanstack/react-router";
import { canonicalAndAlternates } from "@/lib/hreflang";
import { PageSection } from "@/components/site/PageSection";
import { CheckpointPhoto } from "@/components/site/CheckpointPhoto";
import {
  Callout,
  FaqList,
  H2,
  H3,
  P,
  SITE,
  UL,
  faqSchema,
  medicalWebPageSchema,
  type Faq,
} from "@/components/site/InsuranceProse";
import { TelehealthCta } from "@/components/site/TelehealthCta";

const TITLE =
  "GLP-1 Coverage in North Carolina (2026) | Virginia Bariatric";
const DESCRIPTION =
  "What NC Medicaid, the State Health Plan, employer, marketplace, TRICARE, and Medicare plans require for Zepbound, Wegovy, and Saxenda in North Carolina.";
const URL = `${SITE}/medical-weight-loss/insurance/nc`;
const NAME = "Weight-Loss Medication Coverage in North Carolina";
const REVIEWED = "October 25, 2026";
const REVIEWED_ISO = "2026-10-25";

type PlanRow = {
  plan: string;
  planNote: string;
  covers: string;
  bmi: string;
  step: string;
  renewal: string;
};

const PLANS: PlanRow[] = [
  {
    plan: "NC Medicaid",
    planNote: "Fee-for-service and all managed care plans",
    covers: "Yes — restored Dec. 12, 2025",
    bmi: "Adults: 30+, or 27+ with a weight-related condition. Age 45+: 27+ with established cardiovascular disease. Ages 12–17: 95th percentile, or 30+, or 85th percentile with a related condition",
    step: "Yes. Wegovy is preferred. Zepbound and Saxenda require a documented trial and failure of Wegovy, or a reason you cannot take it",
    renewal:
      "Adults: 5% of starting weight lost and maintained. Teens: more than 4% BMI reduction",
  },
  {
    plan: "NC State Health Plan",
    planNote: "State employees, teachers, university staff, retirees",
    covers: "No — not for weight loss",
    bmi: "Not applicable. The weight-loss benefit was removed in April 2024 and not restored for the 2027 plan year.",
    step: "Not applicable",
    renewal:
      "Not applicable. GLP-1s remain covered for type 2 diabetes",
  },
  {
    plan: "Employer plans",
    planNote: "Blue Cross NC, UnitedHealthcare, Aetna, Cigna",
    covers: "Depends on your employer, not your carrier",
    bmi: "Where covered, typically 30+, or 27+ with a weight-related condition",
    step: "Common. Many plans require a preferred agent first, and some require a documented supervised weight-management program",
    renewal: "Usually 5% weight loss at 3–6 months, then annually",
  },
  {
    plan: "NC Marketplace and small group",
    planNote: "Individual ACA plans",
    covers: "Usually no",
    bmi: "Most 2026 NC marketplace formularies list GLP-1s for diabetes only. Wegovy and Zepbound are generally excluded by benefit design",
    step: "Not applicable where excluded",
    renewal: "Not applicable where excluded",
  },
  {
    plan: "TRICARE",
    planNote: "Prime and Select",
    covers: "Yes",
    bmi: "Set by the TRICARE formulary criteria for each drug; prior authorization is required",
    step: "Yes, per formulary. Must be prescribed by a TRICARE-authorized provider and filled at a network pharmacy",
    renewal:
      "Per formulary criteria. Non-Prime/Select beneficiaries lost eligibility Aug. 31, 2025",
  },
  {
    plan: "Medicare",
    planNote: "GLP-1 Bridge, Part D",
    covers: "Yes — new as of July 1, 2026",
    bmi: "Eligibility is set by CMS; check Medicare.gov/glp1bridge",
    step: "Set by CMS for the demonstration",
    renewal:
      "$50 per month. The Part D deductible does not apply and the copay does not count toward out-of-pocket. Runs through Dec. 31, 2027",
  },
];

const TIMELINE: { when: string; what: string }[] = [
  {
    when: "April 2024",
    what: "The NC State Health Plan stopped covering GLP-1s for weight loss. Roughly 23,000 members were using them at the time.",
  },
  {
    when: "Aug. 1, 2024",
    what: "NC Medicaid began covering GLP-1s for weight management, made workable by manufacturer rebates.",
  },
  {
    when: "Aug. 31, 2025",
    what: "TRICARE narrowed weight-loss drug eligibility to Prime and Select beneficiaries.",
  },
  {
    when: "Oct. 1, 2025",
    what: "NC Medicaid dropped weight-management coverage, citing state funding shortfalls. Saxenda was discontinued entirely.",
  },
  {
    when: "Dec. 12, 2025",
    what: "NC Medicaid reinstated coverage and restored the clinical criteria that had been in place since Aug. 1, 2024.",
  },
  {
    when: "July 1, 2026",
    what: "The Medicare GLP-1 Bridge launched, offering $50-per-month access through Dec. 31, 2027.",
  },
  {
    when: "July 10, 2026",
    what: "The State Health Plan Board approved the 2027 tiered provider network and awarded Blue Cross NC both the administrative and pharmacy benefit contracts beginning in 2028.",
  },
  {
    when: "Oct. 15 – Dec. 7, 2026",
    what: "Medicare Open Enrollment for the 2027 plan year.",
  },
  {
    when: "Nov. 9 – Dec. 14, 2026",
    what: "FEHB and PSHB Open Season for the 2027 plan year.",
  },
  {
    when: "Jan. 1, 2027",
    what: "The State Health Plan's four-tier provider network takes effect. Separately, Wegovy becomes a Medicare-negotiated drug at $274 per month, so Part D plans must carry it.",
  },
  {
    when: "Jan. 1, 2028",
    what: "Blue Cross NC assumes claims administration and pharmacy benefit management for the State Health Plan.",
  },
];

const BRANDS: { drug: string; wl: string; other: string }[] = [
  { drug: "Tirzepatide", wl: "Zepbound", other: "Mounjaro (type 2 diabetes)" },
  { drug: "Semaglutide", wl: "Wegovy", other: "Ozempic (type 2 diabetes)" },
  { drug: "Liraglutide", wl: "Saxenda", other: "Victoza (type 2 diabetes)" },
];

const FAQS: Faq[] = [
  {
    q: "Is Dr. Fitzer licensed in North Carolina?",
    a: "Yes. He has an active license in good standing.",
  },
  {
    q: "I am a teacher or state employee. Is there any path for me?",
    a: "Not through the State Health Plan's weight-loss benefit, which has been closed since April 2024 and remains unfunded. Three routes are still open. GLP-1s remain covered for type 2 diabetes, so if you have it, that pathway is intact. If you have obstructive sleep apnea, established cardiovascular disease, or MASH, the alternate-indication route may apply. And if a spouse's employer plan covers anti-obesity medications, that plan may be the better option for you.",
  },
  {
    q: "My coworker and I both have Blue Cross. Why was she approved and I was not?",
    a: "Almost certainly because you work for different employers. In a self-funded plan, the employer selects the drug benefit and the insurance company only administers it. Same card, same network, different formulary. Ask your HR or benefits contact for the pharmacy formulary and look specifically for anti-obesity agents.",
  },
  {
    q: "Does a marketplace plan cover these medications in North Carolina?",
    a: "Usually not. Weight-loss drugs are not an essential health benefit under federal rules, and most 2026 North Carolina marketplace formularies list GLP-1s for diabetes while excluding the weight-loss brands. Check your specific plan's formulary before assuming either way — this varies by carrier and by metal tier.",
  },
  {
    q: "Is bariatric surgery covered when medication is not?",
    a: "Often, yes. Surgery and medication are governed by completely separate parts of your benefit, and a plan that excludes anti-obesity drugs may still cover surgery. Commercial criteria in North Carolina generally require a BMI of 40 or higher, or 35 or higher with a significant related condition such as diabetes, hypertension, sleep apnea, or severe osteoarthritis, plus a psychological evaluation within the prior 12 months and documented attempts at medically supervised weight management.",
  },
  {
    q: "Nothing covers it. What are my options?",
    a: "Manufacturer direct-purchase programs have brought cash prices down substantially, and both major manufacturers now sell directly to patients. Older non-GLP-1 medications are far less expensive and work well for some people. And an appeal is worth filing when the denial was clinical rather than benefit-based — a denial for missing documentation is very different from an exclusion, and only the first kind is winnable.",
  },
  {
    q: "Does NC Medicaid cover Zepbound or Wegovy for weight loss?",
    a: "Yes, as of the December 2025 reinstatement, NC Medicaid again covers GLP-1 medications for weight management under prior approval. You will need a documented BMI at or above the policy threshold, participation in a nutrition and physical activity program, and proof of at least 5% weight loss to continue past the initial authorization period. Coverage for other clinical indications, such as sleep apnea or cardiovascular risk reduction, is handled under separate criteria.",
  },
  {
    q: "How is North Carolina coverage different from Virginia coverage?",
    a: "The mechanics are the same in both states — your plan either covers anti-obesity medication or it does not, and if it does, prior authorization decides whether you qualify. The difference is who covers what. North Carolina's State Health Plan closed its weight-loss benefit in April 2024 and has not restored it, while Virginia's state employee plans still cover these drugs at a BMI of 35 or higher. North Carolina Medicaid restored weight-management coverage in December 2025; Virginia Medicaid uses higher BMI thresholds and requires step therapy. Marketplace plans in both states generally exclude weight-loss drugs.",
  },
  {
    q: "I live in North Carolina but work for a Virginia employer. Which state’s rules apply?",
    a: "Your plan's rules apply, not your state of residence. Coverage follows the plan document, which is set by the employer and, for self-funded plans, is governed by federal ERISA rather than state insurance law. If your employer is in Virginia and the plan is self-funded, the Virginia state mandates do not automatically apply either — the employer's chosen formulary controls. Ask HR for the pharmacy formulary and look for the anti-obesity agents section.",
  },
  {
    q: "Can I be treated by Virginia Bariatric if I live in North Carolina?",
    a: "Yes. Dr. Fitzer holds an active North Carolina license, so North Carolina residents can be seen for medical weight-loss care, including prescribing and prior-authorization support. Reach out through the appointments page and we will confirm what your plan requires before your first visit.",
  },
  {
    q: "How long does a North Carolina prior authorization take?",
    a: "Most North Carolina commercial plans decide a standard prior authorization within 72 hours to 15 days of receiving a complete submission, and NC Medicaid pharmacy prior approvals are typically decided faster. The delay is almost always in gathering documentation — weight history, prior medication trials, and proof of a supervised lifestyle program — not in the plan's review itself. Bringing those records to your first appointment is the single biggest thing you can do to speed it up.",
  },
  {
    q: "My North Carolina plan denied coverage. Should I appeal?",
    a: "It depends on the reason for the denial. If the plan excludes anti-obesity medication as a benefit category, an appeal will not succeed, because there is no benefit to approve. If the denial was clinical — missing documentation, an unmet step-therapy requirement, or a BMI recorded incorrectly — the appeal is often winnable, and North Carolina law gives you the right to an external review by an independent reviewer through the Department of Insurance after you exhaust the plan's internal appeals. Read the denial letter carefully; it will state which kind it is.",
  },
];


const SOURCES: string[] = [
  "NC Medicaid, “NC Medicaid to Reinstitute Coverage of GLP-1s for Weight Management,” Dec. 19, 2025 — medicaid.ncdhhs.gov",
  "NC Medicaid, “NC Medicaid to Change Coverage for GLP-1 Weight Management Medications,” Sept. 5, 2025 — medicaid.ncdhhs.gov",
  "NC Medicaid, “Updates to NC Medicaid Coverage for Wegovy and Zepbound for Clinical Indications Other than Weight Loss,” Nov. 4, 2025 — medicaid.ncdhhs.gov",
  "NC Medicaid Pharmacy Prior Approval Request for GLP-1s for Weight Management (clinical criteria, BMI thresholds, and renewal requirements)",
  "NC Medicaid Clinical Coverage Policy 1A-15, Surgery for Clinically Severe or Morbid Obesity — medicaid.ncdhhs.gov",
  "WRAL, “Expected NC budget won’t restore GLP-1 weight-loss drug coverage for state employees, treasurer says,” May 27, 2026",
  "WUNC, “NC State Health Plan’s new deal with CVS Caremark lets it negotiate for GLP-1s for state employees,” Oct. 8, 2025",
  "KFF Health News, “Who Gets Obesity Drugs Covered by Insurance? In North Carolina, It Helps If You’re on Medicaid”",
  "CMS, “Medicare GLP-1 Bridge” — cms.gov/medicare/coverage/prescription-drug-coverage/medicare-glp-1-bridge",
  "CMS, “Coming Soon: CMS to Provide $50 Monthly Access to GLP-1 Medications for Medicare Beneficiaries”",
  "Express Scripts TRICARE Pharmacy Program, GLP-1 and weight-loss medication coverage FAQ",
  "Blue Cross NC Corporate Medical Policy, Bariatric Surgery (reviewed April 2026)",
  "Blue Cross NC Essential Q Health Insurance Marketplace formulary, North Carolina",
  "KFF, 2025 Employer Health Benefits Survey (self-funded plan prevalence)",
];

export const Route = createFileRoute("/medical-weight-loss/insurance/nc")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      ...(import.meta.env.SSR ? [
        { property: "og:title", content: TITLE },
        { property: "og:description", content: DESCRIPTION },
        { property: "og:url", content: URL },
        { property: "og:type", content: "article" },
        { property: "og:locale", content: "en_US" },
        { property: "og:locale:alternate", content: "es_LA" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: TITLE },
        { name: "twitter:description", content: DESCRIPTION },
        { property: "og:image", content: "https://virginiabariatric.com/og/insurance/og-insurance-medications-nc.png" },
        { property: "og:image:width", content: "2400" },
        { property: "og:image:height", content: "1260" },
        { property: "og:image:alt", content: "GLP-1 weight-loss medication coverage in North Carolina: Medicaid, State Health Plan, TRICARE, and Medicare rules — Virginia Bariatric Surgery" },
        { name: "twitter:image", content: "https://virginiabariatric.com/og/insurance/og-insurance-medications-nc.png" },
        { name: "twitter:image:alt", content: "GLP-1 weight-loss medication coverage in North Carolina: Medicaid, State Health Plan, TRICARE, and Medicare rules — Virginia Bariatric Surgery" },
      ] : []),
    ],
    links: [
      ...(import.meta.env.SSR ? canonicalAndAlternates("/medical-weight-loss/insurance/nc") : []),
    ],
    scripts: import.meta.env.SSR ? [
      { type: "application/ld+json", children: JSON.stringify(faqSchema(FAQS, URL)) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          medicalWebPageSchema({
            url: URL,
            name: NAME,
            description: DESCRIPTION,
            hasFaq: true,
            audienceType:
              "North Carolina patients seeking weight-loss medication coverage",
            lastReviewed: REVIEWED_ISO,
          })
        ),
      },
    ] : [],
  }),
  component: NorthCarolinaCoveragePage,
});

function StateSwitcher() {
  return (
    <nav aria-label="Choose your state" className="mt-5 flex justify-center">
      <Link
        to="/medical-weight-loss/insurance"
        preload="intent"
        className="rounded-full bg-brand-logo px-7 py-3 text-sm font-medium text-brand-foreground hover:opacity-90"
      >
        Virginia resident? See VA coverage rules
      </Link>
    </nav>
  );
}


function NorthCarolinaCoveragePage() {
  return (
    <>
      <PageSection className="pt-8 pb-4 text-center">
        <h1 className="text-3xl md:text-5xl font-serif">
          Weight-Loss Medication Coverage in North Carolina
        </h1>
        <p className="mt-4 max-w-3xl mx-auto text-muted-foreground leading-[1.6]">
          What different insurers require for Zepbound, Wegovy, and Saxenda &mdash; and the
          backdoor most people miss.
        </p>
        <TelehealthCta />

        <StateSwitcher />
        <p className="mt-4 text-sm text-muted-foreground">
          Reviewed by{" "}
          <Link to="/about" className="text-brand-deep underline hover:no-underline">
            Matthew Fitzer, MD, FACS
          </Link>{" "}
          &middot; Last reviewed{" "}
          <time dateTime={REVIEWED_ISO}>{REVIEWED}</time>
        </p>
      </PageSection>

      <PageSection maxWidth="4xl" className="pb-4">
        <Callout>
          <strong>If NC Medicaid denied you in late 2025, apply again.</strong>
          <span className="mt-3 block">
            Between Oct. 1 and Dec. 12, 2025, NC Medicaid did not cover GLP-1s for weight
            management. Coverage came back on Dec. 12, 2025, under the same clinical criteria that
            were in place before the cut.
          </span>
          <span className="mt-3 block">
            Many people who were denied during that window never found out it was reversed. If you
            stopped treatment then, talk to your prescriber about restarting &mdash; and mention the
            gap, since a lower starting dose is often appropriate after a lapse.
          </span>
        </Callout>

        <P>
          In North Carolina, whether your insurance pays for a weight-loss medication has very
          little to do with your health and almost everything to do with who writes your plan. Two
          people with the same BMI, the same blood pressure, and the same insurance card can get
          opposite answers. It depends on whether their employers opted to include it. It is a
          frustrating situation, but it is navigable.
        </P>
        <P>
          This page explains how coverage actually works in NC, what each major plan requires, and
          what to do when the answer is no.
        </P>

        <H2 id="two-ways-in">There are two ways in</H2>

        <H3>The front entrance: two checkpoints</H3>
        <P>
          Getting a weight-loss medication covered means clearing two separate checkpoints. Most
          people only know about the second one.
        </P>

        <CheckpointPhoto />

        <P>

          <strong>Checkpoint 1 &mdash; Does your plan cover weight-loss drugs at all?</strong> This
          is a benefit design question, decided before any doctor is involved. If your plan excludes
          anti-obesity medications as a category, no amount of clinical documentation will change
          the answer. This is where most North Carolina denials happen.
        </P>
        <P>
          <strong>Checkpoint 2 &mdash; Do you meet the clinical criteria?</strong> You have the
          benefit. Great news. Now you need your prescriber (possibly us) to submit a prior
          authorization showing you meet the plan&rsquo;s medical requirements. We will help you
          clear this checkpoint.
        </P>

        <H3>The backdoor</H3>
        <P>
          The same exact drug is often sold under different brand names for different diseases. Such
          is the case with semaglutide and tirzepatide. Both have a brand-named version approved for
          weight loss (Wegovy and Zepbound) and a different brand approved to treat diabetes
          (Ozempic and Mounjaro). Lots of plans that exclude the weight-loss brand cover the one for
          diabetes.
        </P>
        <P>
          Our calling it a &ldquo;backdoor&rdquo; is tongue-in-cheek. This route is not a true
          loophole; it is the medication being prescribed for its labeled indication. There is a{" "}
          <a href="#backdoor" className="text-brand-deep underline hover:no-underline">
            full explanation further down this page
          </a>
          .
        </P>

        <H2 id="prior-authorization">What a prior authorization actually asks for</H2>
        <P>
          Criteria vary by plan, but nearly every prior authorization in North Carolina is built
          from the same seven pieces. Knowing them in advance is the difference between a two-week
          approval and a three-month appeal.
        </P>
        <ol className="mt-3 list-decimal space-y-2 pl-6 text-foreground/85 leading-[1.6]">
          <li>
            <strong>A BMI threshold.</strong> Usually 30 or higher, or 27 or higher if you also have
            a weight-related condition.
          </li>
          <li>
            <strong>A qualifying condition.</strong> High blood pressure, type 2 diabetes,
            obstructive sleep apnea, cardiovascular disease, or abnormal cholesterol are the ones
            that count most often.
          </li>
          <li>
            <strong>Documented lifestyle modification.</strong> Plans want to see that you are on
            &mdash; and will stay on &mdash; a structured nutrition and physical activity plan. Some
            want a specific number of months documented before they will approve anything.
          </li>
          <li>
            <strong>Step therapy.</strong> Many plans require you to try their preferred drug first
            and document that it did not work or that you cannot take it.
          </li>
          <li>
            <strong>An age minimum.</strong> Most adult criteria start at 18. NC Medicaid has
            separate criteria for ages 12 through 17.
          </li>
          <li>
            <strong>Proof it is working.</strong> Renewal almost always requires documented weight
            loss &mdash; commonly 5% of your starting weight &mdash; maintained over time.
          </li>
          <li>
            <strong>No overlapping GLP-1s.</strong> You cannot be approved for two of these
            medications at once, and standard contraindications apply.
          </li>
        </ol>

        <H2 id="plans">North Carolina plans at a glance</H2>
        <P>
          Coverage rules change frequently. Verify anything here against your own plan documents
          before making decisions.
        </P>

        <ul className="mt-4 space-y-4 md:hidden">
          {PLANS.map((p) => (
            <li key={p.plan} className="rounded-2xl border border-border bg-card p-4">
              <h3 className="font-medium text-brand-mid">{p.plan}</h3>
              <p className="mt-1 text-xs text-muted-foreground">{p.planNote}</p>
              <dl className="mt-3 space-y-2 text-sm">
                {[
                  ["Covers weight-loss meds?", p.covers],
                  ["BMI required", p.bmi],
                  ["Step therapy", p.step],
                  ["Renewal", p.renewal],
                ].map(([label, value]) => (
                  <div key={label}>
                    <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      {label}
                    </dt>
                    <dd className="text-foreground/85 leading-[1.6]">{value}</dd>
                  </div>
                ))}
              </dl>
              {p.plan === "Medicare" && (
                <p className="mt-2 text-sm">
                  Two paths apply in 2027 — see our{' '}
                  <Link to="/medical-weight-loss/insurance/medicare-tricare-fehb" className="text-brand-deep underline hover:no-underline">
                    Medicare, TRICARE and FEHB guide
                  </Link>.
                </p>
              )}
            </li>
          ))}
        </ul>

        <div className="mt-4 hidden overflow-x-auto md:block">
          <table className="w-full min-w-[48rem] border-collapse text-sm">

            <caption className="sr-only">
              Weight-loss medication coverage by North Carolina plan
            </caption>
            <thead>
              <tr className="bg-brand-soft/60 text-left">
                <th scope="col" className="border border-border p-2.5">Plan</th>
                <th scope="col" className="border border-border p-2.5">Covers weight-loss meds?</th>
                <th scope="col" className="border border-border p-2.5">BMI required</th>
                <th scope="col" className="border border-border p-2.5">Step therapy</th>
                <th scope="col" className="border border-border p-2.5">Renewal</th>
              </tr>
            </thead>
            <tbody>
              {PLANS.map((p) => (
                <tr key={p.plan} className="align-top">
                  <th scope="row" className="border border-border p-2.5 text-left font-medium">
                    {p.plan}
                    <span className="mt-1 block text-xs font-normal text-muted-foreground">
                      {p.planNote}
                    </span>
                  </th>
                  <td className="border border-border p-2.5">{p.covers}</td>
                  <td className="border border-border p-2.5">{p.bmi}</td>
                  <td className="border border-border p-2.5">{p.step}</td>
                  <td className="border border-border p-2.5">
                    {p.renewal}
                    {p.plan === "Medicare" && (
                      <p className="mt-2">
                        Two paths apply in 2027 — see our{' '}
                        <Link to="/medical-weight-loss/insurance/medicare-tricare-fehb" className="text-brand-deep underline hover:no-underline">
                          Medicare, TRICARE and FEHB guide
                        </Link>.
                      </p>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <P>
          <strong>A note on the employer row:</strong> roughly two-thirds of Americans with
          job-based coverage are in self-funded plans, where the employer &mdash; not the insurance
          company &mdash; decides which drugs are covered. This is why two Blue Cross NC members at
          different companies can get opposite answers. Your carrier&rsquo;s name on the card does
          not tell you whether the benefit exists. Your plan&rsquo;s formulary does.
        </P>

        <H2 id="state-health-plan">NC State Health Plan (state employees, teachers, retirees)</H2>
        <P>
          Weight-loss GLP-1 coverage remains closed. The benefit was removed in April 2024 and was not restored for the 2027 plan year. GLP-1s remain covered for type 2 diabetes.
        </P>

        <H3>What changes January 1, 2027</H3>
        <P>
          A four-tier provider network replaces the current structure: Preferred, Access, Non-Preferred, and Out-of-Network. UNC Health and Novant Health have signed Preferred Provider agreements. Most providers fall into the Access tier, which keeps your current benefit level, and all out-of-state providers are Access tier. Members who stay with a Non-Preferred provider will pay significantly more. You can switch your primary care provider to a Preferred Provider only after January 1, 2027.
        </P>
        <P>
          CVS Caremark remains the pharmacy benefit manager, and the Plan continues to use a closed formulary that changes quarterly — so a drug covered in January may not be covered in July. Check before each refill rather than assuming continuity.
        </P>
        <P>
          Further out, Blue Cross NC takes over both claims administration and pharmacy benefit management on January 1, 2028.
        </P>

        <H3>What does not change</H3>
        <P>
          The Lantern surgical benefit continues in 2027. Members pay $0 — no deductible, no copay, no coinsurance — for more than 1,500 planned, non-emergency procedures when they use a Lantern provider. Bariatric surgery is among them, and since January 1, 2026 it has been covered only when performed by a Lantern surgeon.
        </P>

        <Callout>
          <strong>The contrast is worth stating plainly.</strong>
          <P>
            If you are a North Carolina state employee, teacher, or retiree, the Plan will not pay for your weight-loss medication — but it will pay the entire cost of bariatric surgery.
          </P>
          <P>
            Those are two different benefits with two different budgets, and a closed door on one tells you nothing about the other.
          </P>
        </Callout>

        <H2 id="dates">Dates that matter in North Carolina</H2>
        <P>
          This state has seen more churn than most. If you were told no in the past two years, the
          answer may have changed.
        </P>
        <dl className="mt-4 space-y-3">
          {TIMELINE.map((t) => (
            <div
              key={t.when}
              className="rounded-2xl border border-border bg-card p-4 sm:flex sm:gap-4"
            >
              <dt className="font-medium text-brand-mid sm:w-40 sm:shrink-0">{t.when}</dt>
              <dd className="mt-1 text-foreground/85 leading-[1.6] sm:mt-0">{t.what}</dd>
            </div>
          ))}
        </dl>

        <H2 id="faqs">Common questions</H2>
        <FaqList faqs={FAQS} />

        <H2 id="backdoor">The backdoor: same molecule, different label</H2>
        <P>
          This is the part most patients never hear about, and it is the single most useful thing on
          this page.
        </P>
        <P>
          The active drug in Zepbound is tirzepatide. The active drug in Mounjaro is also
          tirzepatide. They are the same medication, made by the same company, at the same doses.
          What differs is the FDA-approved indication printed on the box &mdash; and insurance plans
          write their coverage rules against the indication, not the molecule.
        </P>

        <ul className="mt-4 space-y-3 sm:hidden">
          {BRANDS.map((b) => (
            <li key={b.drug} className="rounded-2xl border border-border bg-card p-4 text-sm">
              <p className="font-medium text-brand-mid">{b.drug}</p>
              <p className="mt-2 text-foreground/85 leading-[1.6]">
                <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Sold for weight loss as
                </span>
                <br />
                {b.wl}
              </p>
              <p className="mt-2 text-foreground/85 leading-[1.6]">
                <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Same molecule, other indication
                </span>
                <br />
                {b.other}
              </p>
            </li>
          ))}
        </ul>

        <div className="mt-4 hidden overflow-x-auto sm:block">
          <table className="w-full min-w-[32rem] border-collapse text-sm">

            <caption className="sr-only">
              Weight-loss brands and their same-molecule diabetes equivalents
            </caption>
            <thead>
              <tr className="bg-brand-soft/60 text-left">
                <th scope="col" className="border border-border p-2.5">Active drug</th>
                <th scope="col" className="border border-border p-2.5">Sold for weight loss as</th>
                <th scope="col" className="border border-border p-2.5">
                  Same molecule, other indication
                </th>
              </tr>
            </thead>
            <tbody>
              {BRANDS.map((b) => (
                <tr key={b.drug} className="align-top">
                  <th scope="row" className="border border-border p-2.5 text-left font-medium">
                    {b.drug}
                  </th>
                  <td className="border border-border p-2.5">{b.wl}</td>
                  <td className="border border-border p-2.5">{b.other}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <P>
          That means a plan that flatly excludes weight-loss drugs may cover the identical
          medication when it is prescribed for a condition the plan does cover. Four pathways come
          up most often.
        </P>

        <H3>1. Type 2 diabetes</H3>
        <P>
          If you have type 2 diabetes, Ozempic and Mounjaro are covered by nearly every plan in
          North Carolina, including the State Health Plan. This is the widest door.
        </P>

        <H3>2. Obstructive sleep apnea</H3>
        <P>
          Zepbound is FDA-approved for moderate to severe obstructive sleep apnea in adults with
          obesity. NC Medicaid covers it for this indication specifically. If you snore heavily,
          wake unrested, or have been told you stop breathing at night, a sleep study is worth
          pursuing on its own merits &mdash; and it may open this door.
        </P>

        <H3>3. Established cardiovascular disease</H3>
        <P>
          Wegovy is approved to reduce cardiovascular risk in adults with established cardiovascular
          disease and obesity or overweight. NC Medicaid covers it for this indication, and many
          commercial plans do as well, including some that exclude it for weight loss alone.
        </P>

        <H3>4. MASH (fatty liver disease)</H3>
        <P>
          Wegovy is approved for metabolic dysfunction-associated steatohepatitis, and NC Medicaid
          covers it for that use. If you have elevated liver enzymes or a fatty liver noted on
          imaging, this is worth investigating.
        </P>

        <Callout>
          <strong>Where this route stops.</strong>
          <span className="mt-3 block">
            You have to actually have the condition. A prescriber cannot document a diagnosis you do
            not have, and attempting it is insurance fraud &mdash; for you and for the physician.
          </span>
          <span className="mt-3 block">
            What you can reasonably do is make sure you have been properly evaluated. A great many
            people carrying extra weight have undiagnosed sleep apnea, prediabetes, or fatty liver
            and have simply never been tested. Getting an accurate picture of your health is good
            medicine first. If it also opens a coverage pathway, that is a legitimate consequence of
            an honest workup.
          </span>
        </Callout>

        <H2 id="first-appointment">What to bring to your first appointment</H2>
        <P>
          Approvals move faster when the documentation is ready on day one. If you can, gather:
        </P>
        <UL>
          <li>
            Your insurance card, front and back, plus the name of your employer if your coverage is
            job-based
          </li>
          <li>
            Your plan&rsquo;s prescription drug formulary, if you can get it from HR or the member
            portal
          </li>
          <li>
            Weight history &mdash; highest weight, current weight, and any records of past
            weight-loss attempts
          </li>
          <li>
            A list of your current medications and any diagnoses, especially blood pressure,
            cholesterol, blood sugar, and sleep problems
          </li>
          <li>Recent lab work if you have it, particularly A1c, lipids, and liver enzymes</li>
          <li>
            Any prior denial letters &mdash; these state exactly which criterion was not met, which
            tells us what to fix
          </li>
        </UL>

        <H2 id="sources">Sources</H2>
        <ul className="mt-3 list-disc space-y-2 pl-6 text-sm text-muted-foreground leading-[1.6] break-words [overflow-wrap:anywhere]">
          {SOURCES.map((s) => (
            <li key={s}>{s}</li>
          ))}
        </ul>

        <p className="mt-6 text-sm italic text-muted-foreground leading-[1.6]">
          This page is general information about insurance coverage, not medical advice and not a
          guarantee of benefits. Coverage rules change often and vary by individual plan, even
          within the same insurance company. Always verify your specific benefits with your plan
          before making treatment decisions. Reviewed {REVIEWED}.
        </p>
      </PageSection>

      <section className="py-8 bg-brand-soft/30">
        <div className="mx-auto max-w-3xl px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-serif">
            Talk to a provider licensed in North Carolina
          </h2>
          <p className="mt-3 text-foreground/85 leading-[1.6]">
            We are licensed to treat patients in both North Carolina and Virginia, and we handle the
            prior authorization work as part of your care. If you are not sure what your plan
            covers, that is a normal place to start &mdash; bring your card and we will find out
            together.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link
              to="/appointments"
              className="rounded-full bg-primary px-7 py-3 text-sm font-medium text-primary-foreground hover:opacity-90"
            >
              Request a consultation
            </Link>
            <Link
              to="/medical-weight-loss/medications"
              className="rounded-full bg-brand-logo px-7 py-3 text-sm font-medium text-brand-foreground hover:opacity-90"
            >
              Weight-loss medications
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
